import { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Search, X } from 'lucide-react';

const STATUSES = ['New', 'Shortlisted', 'Contacted', 'Selected', 'Not Selected'];
const STATUS_STYLE = {
  New: 'bg-blue-100 text-blue-700', Shortlisted: 'bg-amber-100 text-amber-700', Contacted: 'bg-teal-100 text-teal-700',
  Selected: 'bg-green-100 text-green-700', 'Not Selected': 'bg-slate-200 text-slate-600',
};
const PROGRAMS = { 'campus-ambassador': ['Campus Ambassador', 'bg-indigo-50 text-indigo-700'], 'founding-creator': ['Founding Creator', 'bg-fuchsia-50 text-fuchsia-700'] };
const CSV_COLS = [
  ['Application ID', 'id'], ['Opportunity', 'program'], ['Submitted', 'created_at'], ['Status', 'status'], ['Name', 'full_name'], ['Email', 'email'],
  ['WhatsApp', 'whatsapp'], ['State', 'state'], ['City', 'city'], ['College', 'college'], ['Course', 'course'],
  ['Year', 'year_of_study'], ['LinkedIn', 'linkedin'], ['Clubs', 'clubs'], ['Why', 'why'], ['Engagement Plan', 'engage'],
  ['Instagram / LinkedIn Profile', 'profile_url'], ['Best Content', 'content_url'], ['Creative Idea', 'idea'],
];
const ProgramPill = ({ p }) => <span className={`inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${PROGRAMS[p]?.[1]}`}>{PROGRAMS[p]?.[0] || p}</span>;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const csvCell = (v) => {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};

const StatusSelect = ({ app, canEdit, onChange, testid }) => (
  <select data-testid={testid} value={app.status} disabled={!canEdit} onChange={(e) => onChange(app, e.target.value)}
    className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 ${STATUS_STYLE[app.status]}`}>
    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

const Detail = ({ app, onClose, canEdit, onStatus }) => (
  <div data-testid="ca-detail-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{app.full_name}</h3>
          <p className="text-sm text-slate-500">Ref {app.id.slice(0, 8).toUpperCase()} • Submitted {fmt(app.created_at)}</p>
        </div>
        <button data-testid="ca-detail-close-btn" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <div className="flex items-center gap-3"><ProgramPill p={app.program} /><StatusSelect app={app} canEdit={canEdit} onChange={onStatus} testid="ca-detail-status-select" /></div>
      {app.program === 'founding-creator' ? (
        <>
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            {[['Email', app.email], ['WhatsApp', app.whatsapp], ['Instagram / LinkedIn', app.profile_url], ['Best content', app.content_url]].map(([k, v]) => (
              <div key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 break-words">{v && /^(https?:\/\/|www\.|[a-z0-9-]+\.)/i.test(v) && k !== 'Email' && k !== 'WhatsApp'
                ? <a href={/^https?:\/\//i.test(v) ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-teal-600 underline">{v}</a> : (v || '—')}</dd></div>
            ))}
          </dl>
          <div className="bg-slate-50 rounded-xl p-4"><p className="text-sm font-medium text-slate-700">One creative idea for growing Elevate Careers India</p><p className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{app.idea}</p></div>
        </>
      ) : (<>
      <dl className="grid sm:grid-cols-2 gap-3 text-sm">
        {[['Email', app.email], ['WhatsApp', app.whatsapp], ['State', app.state], ['City', app.city], ['College', app.college],
          ['Course', app.course], ['Year', app.year_of_study], ['LinkedIn', app.linkedin], ['Clubs / Activities', app.clubs]].map(([k, v]) => (
          <div key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 break-words">{v || '—'}</dd></div>
        ))}
      </dl>
      {[['Why would you like to become an Elevate Campus Ambassador?', app.why], ['How would you engage students at your college?', app.engage]].map(([q, a]) => (
        <div key={q} className="bg-slate-50 rounded-xl p-4">
          <p className="text-sm font-medium text-slate-700">{q}</p>
          <p className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{a}</p>
        </div>
      ))}
      </>)}
    </div>
  </div>
);

export const CampusAmbassadorsAdmin = ({ authFetch, apiUrl, canEdit }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ program: '', state: '', city: '', college: '', status: '' });
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${apiUrl}/api/admin/campus-ambassadors`);
      if (res.ok) setApps((await res.json()).applications || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => ({
    state: [...new Set(apps.map((a) => a.state).filter(Boolean))].sort(),
    city: [...new Set(apps.map((a) => a.city).filter(Boolean))].sort(),
    college: [...new Set(apps.map((a) => a.college).filter(Boolean))].sort(),
  }), [apps]);

  const inProgram = filters.program ? apps.filter((a) => a.program === filters.program) : apps;
  const shown = apps.filter((a) => {
    if (Object.entries(filters).some(([k, v]) => v && a[k] !== v)) return false;
    const s = q.trim().toLowerCase();
    return !s || [a.full_name, a.email, a.college, a.city, a.state, a.whatsapp, a.profile_url].some((v) => (v || '').toLowerCase().includes(s));
  });

  const updateStatus = async (app, status) => {
    const res = await authFetch(`${apiUrl}/api/admin/campus-ambassadors/${app.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (!res.ok) { alert('Could not update status'); return; }
    setApps((list) => list.map((a) => (a.id === app.id ? { ...a, status } : a)));
    setOpen((o) => (o && o.id === app.id ? { ...o, status } : o));
  };

  const exportCsv = () => {
    const rows = [CSV_COLS.map(([h]) => csvCell(h)).join(','), ...shown.map((a) => CSV_COLS.map(([, k]) => csvCell(a[k])).join(','))];
    const url = URL.createObjectURL(new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: `careers-applications-${new Date().toISOString().slice(0, 10)}.csv` });
    link.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="ca-admin" className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3" data-testid="share-kit">
        {[['founding-creator', 'Founding Creator'], ['campus-ambassador', 'Campus Ambassador']].map(([slug, title]) => (
          <div key={slug} className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-wrap items-center gap-3" data-testid={`share-kit-${slug}`}>
            <img src={`/share/${slug}-post.png`} alt={`${title} Instagram post`} className="w-14 h-[70px] rounded-lg object-cover border border-slate-200" />
            <div className="flex-1 min-w-[160px]">
              <p className="text-sm font-semibold text-slate-900">{title}: Instagram share kit</p>
              <p className="text-xs text-slate-500">Links to elevatecareersindia.in/careers</p>
            </div>
            {[['Post', 'post'], ['Story', 'story']].map(([l, k]) => (
              <a key={k} href={`/share/${slug}-${k}.png`} download={`${slug}-${k}.png`} data-testid={`share-download-${slug}-${k}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                <Download className="w-4 h-4" /> {l}
              </a>
            ))}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl p-4 border border-slate-100"><p className="text-xs text-slate-500">Total</p><p data-testid="ca-total" className="text-2xl font-bold text-slate-900">{inProgram.length}</p></div>
        {STATUSES.map((s) => (
          <div key={s} className="bg-white rounded-xl p-4 border border-slate-100"><p className="text-xs text-slate-500">{s}</p><p className="text-2xl font-bold text-slate-900">{inProgram.filter((a) => a.status === s).length}</p></div>
        ))}
      </div>
      <div className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="ca-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, college, city..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <select data-testid="ca-filter-program" value={filters.program} onChange={(e) => setFilters((f) => ({ ...f, program: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">All opportunities</option>
          {Object.entries(PROGRAMS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {['state', 'city', 'college'].map((k) => (
          <select key={k} data-testid={`ca-filter-${k}`} value={filters[k]} onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm max-w-[180px]">
            <option value="">All {k === 'college' ? 'colleges' : k === 'city' ? 'cities' : 'states'}</option>
            {options[k].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        ))}
        <select data-testid="ca-filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="ca-refresh-btn" onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        <button data-testid="ca-export-csv-btn" onClick={exportCsv} disabled={!shown.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm disabled:opacity-40 hover:bg-teal-700 transition-colors">
          <Download className="w-4 h-4" /> Export CSV ({shown.length})
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 bg-slate-50">
            <tr>{['Submitted', 'Opportunity', 'Name', 'College / Profile', 'City / State', 'Year', 'Status', ''].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((a, i) => (
              <tr key={a.id} data-testid={`ca-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 whitespace-nowrap text-slate-500">{fmt(a.created_at)}</td>
                <td className="p-3" data-testid={`ca-program-${i}`}><ProgramPill p={a.program} /></td>
                <td className="p-3"><p className="font-medium text-slate-900">{a.full_name}</p><p className="text-xs text-slate-500">{a.email}</p></td>
                <td className="p-3 max-w-[220px]">{a.program === 'founding-creator' ? <p className="text-xs text-slate-600 truncate" title={a.profile_url}>{a.profile_url}</p> : <>{a.college}<p className="text-xs text-slate-500">{a.course}</p></>}</td>
                <td className="p-3">{a.city || '—'}<p className="text-xs text-slate-500">{a.state}</p></td>
                <td className="p-3 whitespace-nowrap">{a.year_of_study || '—'}</td>
                <td className="p-3"><StatusSelect app={a} canEdit={canEdit} onChange={updateStatus} testid={`ca-status-select-${i}`} /></td>
                <td className="p-3"><button data-testid={`ca-view-btn-${i}`} onClick={() => setOpen(a)} className="text-teal-600 hover:underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <p data-testid="ca-empty" className="p-6 text-center text-slate-500">{loading ? 'Loading…' : 'No applications found.'}</p>}
      </div>
      {open && <Detail app={open} onClose={() => setOpen(null)} canEdit={canEdit} onStatus={updateStatus} />}
    </div>
  );
};
